import { getPrivateKey } from './user.service';
import Affiliates from '../sidechain/handles/affiliateHandle';
import Toi from '../sidechain/handles/toiHandle';
import Products from '../sidechain/handles/productHandle';
import CustomError from './errors/CustomError';
import { status_response } from './constants';
import { convertWeitoEther, getLocalStorage, setLocalStorage } from '../utils/commonUtil';

/**
 * Check if product has in recommend list
 * @param {*} productId order id
 * @return {*} bool value
 */
const checkProductInRecommendList = async function(productId) {
  try {
    const privateKey = await getPrivateKey();
    const affiliateHandle = new Affiliates(privateKey);
    const result = await affiliateHandle.checkProductInRecommendList(productId);
    return {
      status: status_response.success,
      result: {
        data: result,
        messageId: 'success'
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId
      }
    };
  }
};

/**
 * Add product to recommend list
 * @param productId
 */
const addProductRecommend = async function(productId) {
  try {
    const privateKey = await getPrivateKey();
    const affiliateHandle = new Affiliates(privateKey);
    const productInList = await affiliateHandle.checkProductInRecommendList(productId);
    if (productInList) {
      throw new CustomError('Product already in recommend list.');
    }
    // Add to list
    const data = await affiliateHandle.addProductRecommend(productId);
    return {
      status: status_response.success,
      result: {
        data: data,
        messageId: 'EF2194_Content'
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: 'EF2195_Content'
      }
    };
  }
};

/**
 * Get overview of affiliator
 * @param {*} productId
 * @return overview
 * sample data for overview:
 * {
 *   numOfRecommend: {
 *      currentUsed: 30,
 *      total: 45
 *   },
 *   toiBalance: 18,
 *   statisticReview: {
 *      numOfGood: 50,
 *      numOfBad: 8
 *   }
 * }
 */
const getOverview = async function() {
  try {
    let privateKey = await getPrivateKey();
    const affiliateHandle = new Affiliates(privateKey);
    const toiHandle = new Toi(privateKey);
    const numOfRecommend = {};
    numOfRecommend.currentUsed = await affiliateHandle.getCurrentNumOfRecommendUsed();
    numOfRecommend.total = await affiliateHandle.getTotalNumOfRecommend();
    const toiBalance = await toiHandle.balanceOf();
    const statisticReview = await affiliateHandle.getStatisticAffiliate();
    return {
      status: status_response.success,
      result: {
        data: {
          numOfRecommend,
          toiBalance: convertWeitoEther(toiBalance, 5),
          statisticReview
        },
        messageId: 'success'
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId
      }
    };
  }
};

/**
 * Get list product in recommend list
 * @param {*} productId
 * @param {*} page
 * @param {*} limit
 * @param {*} sortByDate
 * @return array of review
 * sample data return:
 * {
 *  affiliates: [{
 *  }],
 *  count: 1
 * }
 */
const getAffiliates = async function(page, limit, sortByDate = 1) {
  try {
    let privateKey = await getPrivateKey();
    const affiliateHandle = new Affiliates(privateKey);
    const productHandle = new Products(privateKey);
    const result = await affiliateHandle.getRecommendIds(page, limit, sortByDate);
    const promises = result.data.map(async item => {
      const product = await productHandle.getProductById(item);
      product.statistic = await affiliateHandle.getStatisticAffiliateOfProduct(item);
      return product;
    });
    const affiliates = await Promise.all(promises);
    return {
      status: status_response.success,
      result: {
        data: {
          affiliates: affiliates,
          total: result.total
        },
        messageId: 'success'
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId
      }
    };
  }
};

const setStorageAffiliator = function(productId, affiliator) {
  if (productId == null || typeof productId === 'undefined' || !affiliator) return;
  const affiliate = getLocalStorage('affiliate', 'json') || {};
  affiliate[productId] = affiliator;
  setLocalStorage('affiliate', affiliate);
};

const getStorageAffiliator = function(productId) {
  const affiliate = getLocalStorage('affiliate', 'json');

  return productId !== null && affiliate && affiliate[productId] ? affiliate[productId] : null;
};

const removeStorageAffiliator = function(productId) {
  const affiliate = getLocalStorage('affiliate', 'json');

  if (productId !== null && affiliate && affiliate[productId]) {
    delete affiliate[productId];
  }

  setLocalStorage('affiliate', affiliate);
};

export {
  checkProductInRecommendList,
  addProductRecommend,
  getOverview,
  getAffiliates,
  setStorageAffiliator,
  getStorageAffiliator,
  removeStorageAffiliator
};
